// src/utils/entitlements.js — lógica pura de tier/quota (client, UX-only).
//
// NÃO é autoridade de quota (isso é server, em functions/utils/entitlements.js).
// Aqui só espelhamos a lógica para EXIBIÇÃO (restante, blur, "X/Y hoje").
// Mantém os mesmos números via src/config/planoConfig.js.

import {
  TIERS,
  PRECEDENCIA_TIER,
  TIERS_PAGOS,
  QUOTAS,
  JANELAS,
  VIDA_DESDE_MS,
  TICKET_FIELD,
  TICKET_SALDO_TIPO,
  TICKET_EXTRAS_MAX_DIA,
  FONTE_QUOTA,
  FEATURES,
} from "../config/planoConfig";

// ── Fronteira de dia America/Sao_Paulo via offset fixo −03:00 ────────────────
// 00:00 BRT == 03:00 UTC. Brasil sem horário de verão (DST) desde 2019;
// revisar este offset se o DST voltar.
const BRT_OFFSET_MS = 3 * 60 * 60 * 1000;
const DIA_MS = 24 * 60 * 60 * 1000;

export function getBrtDayStartMs(nowMs = Date.now()) {
  const shifted = new Date(nowMs - BRT_OFFSET_MS);
  const startBrtAsUtc = Date.UTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    0, 0, 0, 0
  );
  return startBrtAsUtc + BRT_OFFSET_MS;
}

// ── JANELA DESLIZANTE — trocar aqui se for migrar para ciclo ancorado ──
export function getJanelaStartMs(janela, nowMs = Date.now()) {
  switch (janela) {
    case JANELAS.DIA_BRT:
      return getBrtDayStartMs(nowMs);
    case JANELAS.ROLLING_30D:
      return nowMs - 30 * DIA_MS;
    case JANELAS.ROLLING_90D:
      return nowMs - 90 * DIA_MS;
    case JANELAS.VIDA:
      return VIDA_DESDE_MS;
    default:
      return nowMs; // desconhecida → janela vazia (não conta nada)
  }
}

export function toMillis(v) {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (typeof v._seconds === "number") return v._seconds * 1000;
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return null;
}

function getByPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

// ── Resolver de tier (espelho de functions/utils/entitlements.js) ────────────
//
// O app NÃO lê PARTNER_MEMBERSHIP (as regras não deixam) nem GYM_CONFIG. O
// vínculo com academia chega pronto em USER.PARCEIRO, escrito só pelo servidor:
//   {TIER: 'VINCULADO'|'VINCULADO_PREMIUM', GYM_CODE, GYM_NAME, LOGO_URL, CITY,
//    STATE_UF, PLANO_ACADEMIA, DESDE, BENEFICIOS}

/** Vínculo ativo num PARTNER_MEMBERSHIP (mantido para quem ainda passa um). */
export function isPartnerActive(pm) {
  return !!pm && pm.ACTIVE === true && typeof pm.REFERRAL_CODE === "string" &&
    pm.REFERRAL_CODE.trim() !== "";
}

// Criador Parceiro com benefícios (USER.CRIADOR é escrito só pelo backend).
export function isCriadorAtivo(criador) {
  const status = criador && criador.STATUS;
  return status === "ATIVA" || status === "ATENCAO";
}

const up = (v) => (typeof v === "string" ? v.trim().toUpperCase() : "");
const tem = (v) => v !== undefined && v !== null && v !== "";
const GATEWAYS_PAGOS = new Set([
  "ASAAS", "STRIPE", "APP_STORE", "APPLE_APP_STORE", "GOOGLE_PLAY", "PLAY_STORE", "KIWIFY",
]);

/** Há sinal de gateway de pagamento? (espelho de functions/utils/assinaturaPaga.js) */
function temGatewayPago(sub) {
  if (!sub || typeof sub !== "object") return false;
  if (GATEWAYS_PAGOS.has(up(sub.PROVEDOR) || up(sub.PROVIDER))) return true;
  if (tem(sub.PAGAMENTO_ID) || tem(sub.PARCELAMENTO_ID) || up(sub.PLAN_FAMILY) === "B2C_ANUAL_12X") {
    return true;
  }
  const pid = typeof sub.PRODUCT_ID === "string" ? sub.PRODUCT_ID : "";
  if (tem(sub.TRANSACTION_ID) || tem(sub.ORIGINAL_TRANSACTION_ID) ||
      pid.startsWith("lesvita_start") || pid.startsWith("prod_")) return true;
  return tem(sub.STRIPE_SUBSCRIPTION_ID) || tem(sub.STRIPE_CUSTOMER_ID);
}

// Tipos que o vínculo com academia gravava; qualquer outro é outra coisa.
const TIPOS_DO_VINCULO = ["PARCEIRO", "GRATUITO", "", undefined, null];

/**
 * O SUBSCRIPTION_INFO é o "Premium de parceiro" antigo, e não uma assinatura
 * da pessoa? Precisa da marca do vínculo (ORIGEM_ATIVACAO GYM_PARTNER ou
 * GYM_LINKED_PREMIUM), do tipo que o vínculo gravava e de nenhum gateway de
 * pagamento (espelho de functions/utils/entitlements.js).
 */
export function assinaturaDeParceiro(si) {
  if (!si || typeof si !== "object") return false;
  const marca = si.ORIGEM_ATIVACAO === "GYM_PARTNER" || si.GYM_LINKED_PREMIUM === true;
  const tipo = typeof si.ASSINATURA_TYPE === "string" ? up(si.ASSINATURA_TYPE) : si.ASSINATURA_TYPE;
  return marca && TIPOS_DO_VINCULO.includes(tipo) && !temGatewayPago(si);
}

/** Tier do USUÁRIO: GRATUITO | PREMIUM | CRIADOR (não olha academia). */
export function resolverTierUsuario({ subscriptionInfo = {}, criador = null } = {}) {
  if (isCriadorAtivo(criador)) return TIERS.CRIADOR;
  const si = subscriptionInfo || {};
  if (si.STATUS === "ATIVO" && si.PLANO && si.PLANO !== "GRATUITO" && !assinaturaDeParceiro(si)) {
    return TIERS.PREMIUM;
  }
  return TIERS.GRATUITO;
}

/** Tier do PARCEIRO a partir do espelho USER.PARCEIRO. */
export function tierParceiroDoEspelho(parceiro) {
  const t = parceiro && parceiro.TIER;
  return t === TIERS.VINCULADO || t === TIERS.VINCULADO_PREMIUM ? t : null;
}

/** O melhor dos dois tiers. */
export function tierEfetivo(tierUser, tierParceiro) {
  let melhor = TIERS.GRATUITO;
  let idx = PRECEDENCIA_TIER.indexOf(melhor);
  [tierUser, tierParceiro].filter(Boolean).forEach((t) => {
    const i = PRECEDENCIA_TIER.indexOf(t);
    if (i >= 0 && i < idx) {
      melhor = t;
      idx = i;
    }
  });
  return melhor;
}

/**
 * Quem é esta conta: as duas dimensões e o efetivo.
 * @param {object} args {subscriptionInfo, criador, parceiro (USER.PARCEIRO)}
 * @return {{tier:string, tierUser:string, tierParceiro:(string|null), beneficios:(object|null)}}
 */
export function resolverPerfilDeAcesso({ subscriptionInfo = {}, criador = null, parceiro = null } = {}) {
  const tierUser = resolverTierUsuario({ subscriptionInfo, criador });
  const tierParceiro = tierParceiroDoEspelho(parceiro);
  const tier = tierEfetivo(tierUser, tierParceiro);
  // Os benefícios da academia só valem quando o tier que está valendo é o dela.
  const beneficios = tierParceiro && tier === tierParceiro ?
    ((parceiro && parceiro.BENEFICIOS) || null) : null;
  return { tier, tierUser, tierParceiro, beneficios };
}

/**
 * Tier efetivo (compatibilidade). `parceiro` é o USER.PARCEIRO; um
 * `partnerMembership` legado ainda conta como vínculo essencial.
 */
export function resolveTierFromData({
  subscriptionInfo = {},
  partnerMembership = null,
  criador = null,
  parceiro = null,
} = {}) {
  const espelho = parceiro ||
    (isPartnerActive(partnerMembership) ? { TIER: TIERS.VINCULADO } : null);
  return resolverPerfilDeAcesso({ subscriptionInfo, criador, parceiro: espelho }).tier;
}

/** O tier tem o pacote pago (sem anúncio, recursos liberados)? */
export function tierPago(tier) {
  return TIERS_PAGOS.includes(tier);
}

/**
 * Quota do tier, com o limite trocado pelo da academia quando ela definiu um.
 * @param {string} feature
 * @param {string} tier
 * @param {object|null} [beneficios] de resolverPerfilDeAcesso
 */
export function quotaFor(feature, tier, beneficios = null) {
  const base = (QUOTAS[feature] || {})[tier] || null;
  if (!base) return null;
  const proprio = beneficios && beneficios.QUOTAS ? beneficios.QUOTAS[feature] : null;
  if (proprio === null || proprio === undefined || !Number.isFinite(Number(proprio))) return base;
  return { limite: Math.max(0, Math.floor(Number(proprio))), janela: base.janela || JANELAS.DIA_BRT };
}

/**
 * Flag de feature (mapaMuscularInteligente, graficosEvolucao, analiseInteligente,
 * ads) com o override da academia.
 */
export function featureLiberada(featureFlag, tier, beneficios = null) {
  if (featureFlag === "ads") {
    if (beneficios && (beneficios.ADS === true || beneficios.ADS === false)) return beneficios.ADS;
    return (FEATURES.ads || {})[tier] === true;
  }
  const proprio = beneficios && beneficios.FEATURES ? beneficios.FEATURES[featureFlag] : null;
  if (proprio === "LIBERADO" || proprio === "BLOQUEADO") return proprio === "LIBERADO";
  return ((FEATURES[featureFlag] || {})[tier]) === "LIBERADO";
}

/**
 * Conta consumos 'JANELA' em uma lista de logs em memória (espelho do server).
 * @param {Array<object>} logs
 * @param {string} tsField  caminho do timestamp (ex.: 'info_analise.data')
 * @param {string} janela
 * @param {number} nowMs
 * @return {number}
 */
export function contarNaJanela(
  logs,
  tsField,
  janela,
  nowMs = Date.now(),
  fonte = FONTE_QUOTA.JANELA
) {
  if (!Array.isArray(logs)) return 0;
  const start = getJanelaStartMs(janela, nowMs);
  let count = 0;
  for (const log of logs) {
    if (!log || log.FONTE_QUOTA !== fonte) continue;
    const ts = toMillis(getByPath(log, tsField));
    if (ts != null && ts >= start) count++;
  }
  return count;
}

/**
 * Restante exibível para uma feature (UX). Espelha a fórmula do server:
 * (limite - usadosJanela) + saldoTicket. Bônus rewarded não é considerado aqui
 * (chega no Prompt 3). Para feature ilimitada retorna null (sem limite).
 *
 * @param {object} args
 * @param {string} args.feature
 * @param {string} args.tier
 * @param {Array<object>} [args.logs]  logs em memória da feature
 * @param {number} [args.ticketDisponivel]
 * @param {number} [args.nowMs]
 * @return {{ ilimitado:boolean, limite:(number|null), usados:number,
 *           restante:(number|null), janela:(string|null), ticketDisponivel:number }}
 */
export function restanteParaFeature({
  feature,
  tier,
  logs = [],
  ticketDisponivel = 0,
  nowMs = Date.now(),
  beneficios = null,
}) {
  const q = quotaFor(feature, tier, beneficios);
  // Feature tem camada de ticket se usa campo do doc USER (TICKET_FIELD) OU
  // saldo comprado em TICKETS_SALDO (TICKET_SALDO_TIPO — ex.: scanfood).
  const temCamadaTicket = !!(TICKET_FIELD[feature] || TICKET_SALDO_TIPO[feature]);
  const saldoTicket = temCamadaTicket ? Number(ticketDisponivel || 0) : 0;

  if (!q || q.limite === null || q.limite === undefined) {
    return {
      ilimitado: true,
      limite: null,
      usados: 0,
      restante: null,
      janela: q ? q.janela || null : null,
      ticketDisponivel: saldoTicket,
    };
  }

  const usados = contarNaJanela(logs, logTsField(feature), q.janela, nowMs);

  // TETO DIÁRIO de tickets COMPRADOS (espelho do gate server + da competição):
  // ter 10 no saldo não significa 10 utilizáveis HOJE. Contamos os consumos já
  // marcados como TICKET na janela e cortamos o restante — assim o número da
  // tela é exatamente o que o gate vai autorizar.
  const tetoExtrasDia = TICKET_EXTRAS_MAX_DIA[feature] ?? null;
  const extrasUsadosDia =
    tetoExtrasDia === null
      ? 0
      : contarNaJanela(
          logs,
          logTsField(feature),
          q.janela,
          nowMs,
          FONTE_QUOTA.TICKET
        );
  const ticket =
    tetoExtrasDia === null
      ? saldoTicket
      : Math.min(saldoTicket, Math.max(0, tetoExtrasDia - extrasUsadosDia));

  const restante = Math.max(0, q.limite - usados) + ticket;
  return {
    ilimitado: false,
    limite: q.limite,
    usados,
    restante,
    janela: q.janela,
    ticketDisponivel: ticket,
    // Diagnóstico p/ a UI: saldo bruto x utilizável hoje.
    saldoTicketTotal: saldoTicket,
    extrasUsadosDia,
    tetoExtrasDia,
  };
}

// tsField por feature (espelha LOG_SOURCES, evita import extra no consumidor).
function logTsField(feature) {
  switch (feature) {
    case "scanbody":
      return "info_analise.data";
    case "scanfood":
      return "createdAt";
    case "geradorTreino":
    case "geradorDieta":
      return "CREATED_AT";
    default:
      return "createdAt";
  }
}
